/** Mirrors `validateSearch` for `/admin/inventory` (see `routes/admin/inventory.tsx`).
 *
 *  Store Inventory kept its active tab in local state, so no part of this
 *  screen could be linked to — a toast saying "see Vendors / AP" was the only
 *  way to send someone somewhere. The tab now lives in the URL, and the vendor
 *  drill-in with it, so "open Sana's Bakes" is a link. */
export type InventoryTab =
  | 'items'
  | 'purchasing'
  | 'vendors'
  | 'alerts'
  | 'history'
  | 'reports'
  // Kitchen flow (lane C) — only rendered when kitchen_flow is on.
  | 'requests'
  | 'locations'
  // Kitchen flow Stage 3 — count sessions per location.
  | 'count'
  // Stage 2C — the waste log (kitchen_flow only).
  | 'waste'
  // Stage 2D — kitchen basics, push a send (kitchen_flow only).
  | 'basics'

export const INVENTORY_TABS: InventoryTab[] = [
  'items',
  'purchasing',
  'vendors',
  'alerts',
  'history',
  'reports',
  'requests',
  'locations',
  'count',
  'waste',
  'basics',
]

export type InventorySearch = {
  tab?: InventoryTab
  /** UUID — open this vendor's profile sheet on the Vendors tab. */
  vendor?: string
  /** UUID — open this purchase order's detail sheet on the Purchasing tab. */
  po?: string
  /** Vendor UUID — open the new-order dialog seeded for this vendor
   *  (the "New order" button on a vendor profile lands here). */
  orderFor?: string
  /** Stock item UUID — open its detail drawer on the Items tab. */
  item?: string
  /** Reports tab date range (DD-MM-YYYY / preset), written by useReportRange.
   *  Whitelisted here so a router navigation (e.g. a tab switch) does not
   *  strip what the hook wrote via history.replaceState (F-FE-N99 / F6). */
  from?: string
  to?: string
  preset?: string
}

/** Legacy tab ids that used to be their own tabs. `vendor-reports` was a whole
 *  tab whose first control was "pick a vendor"; it is now a section inside the
 *  vendor profile, so an old link lands on the Vendors tab instead of 404-ing
 *  into the default. */
const TAB_ALIASES: Record<string, InventoryTab> = {
  'vendor-reports': 'vendors',
  'vendor_reports': 'vendors',
  ap: 'vendors',
  // Categories folded into the Items tab (Manage categories dialog);
  // Movements renamed History. Old links keep landing somewhere sensible.
  categories: 'items',
  movements: 'history',
  // F-CTRL-N75: Market Buys is now "Already bought" in Purchasing.
  market: 'purchasing',
  // Batch 4 (kitchen flow): the store's grouped tab names, so a link can say
  // what the tab bar shows. The old ids stay the canonical ones.
  todo: 'requests',
  stock: 'items',
  buying: 'purchasing',
}

export function parseInventorySearch(raw: Record<string, unknown>): InventorySearch {
  const out: InventorySearch = {}

  const rawTab = typeof raw.tab === 'string' ? raw.tab : undefined
  if (rawTab) {
    if ((INVENTORY_TABS as string[]).includes(rawTab)) {
      out.tab = rawTab as InventoryTab
    } else if (TAB_ALIASES[rawTab]) {
      out.tab = TAB_ALIASES[rawTab]
    }
  }

  const vendor = typeof raw.vendor === 'string' ? raw.vendor.trim() : ''
  if (vendor) {
    out.vendor = vendor
    // A vendor drill-in implies the tab that hosts it, so a bare
    // `?vendor=<id>` link opens the right screen rather than the default tab
    // with an invisible sheet behind it.
    if (!out.tab) out.tab = 'vendors'
  }

  // Same convention for the purchasing drill-ins: bare `?po=` / `?orderFor=`
  // links land on the Purchasing tab with the right thing open.
  const po = typeof raw.po === 'string' ? raw.po.trim() : ''
  if (po) {
    out.po = po
    if (!out.tab) out.tab = 'purchasing'
  }
  const orderFor = typeof raw.orderFor === 'string' ? raw.orderFor.trim() : ''
  if (orderFor) {
    out.orderFor = orderFor
    if (!out.tab) out.tab = 'purchasing'
  }

  const item = typeof raw.item === 'string' ? raw.item.trim() : ''
  if (item) {
    out.item = item
    if (!out.tab) out.tab = 'items'
  }

  // Report-range params pass through untouched — useReportRange owns their
  // format and validation.
  for (const key of ['from', 'to', 'preset'] as const) {
    const v = typeof raw[key] === 'string' ? (raw[key] as string).trim() : ''
    if (v) out[key] = v
  }

  return out
}
